import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, ShieldCheck, BookOpen, FileText, Download, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import { resources } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';

const ResourcesPage = () => {
  usePageTitle("Resources", "Free IT consulting resources, templates, and guides for digital transformation, cybersecurity, ERP planning, and cloud modernization from ElitePlus UAE.");
  const iconMap = {
    FileCheck,
    ShieldCheck,
    BookOpen,
    FileText
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-6">
            Resources
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6">
            Free <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Resources</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Practical tools, templates, and guides to help you plan and execute your IT transformation.
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {resources.map((resource) => {
              const Icon = iconMap[resource.icon] || FileText;
              return (
                <div
                  key={resource.id}
                  data-testid={`resource-card-${resource.id}`}
                  className="group relative p-8 bg-white border border-slate-200 rounded-2xl hover:border-cyan-400/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300"
                >
                  {/* Type Badge */}
                  <span className="absolute top-6 right-6 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-xs font-medium">
                    {resource.type}
                  </span>

                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-cyan-500" />
                    </div>

                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-cyan-600 transition-colors duration-300">
                        {resource.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6">
                        {resource.description}
                      </p>

                      <Button
                        data-testid={`download-btn-${resource.id}`}
                        variant="outline"
                        className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-cyan-400 hover:text-cyan-600 transition-all duration-300 group/btn"
                      >
                        <Download className="w-4 h-4 mr-2 group-hover/btn:scale-110 transition-transform" />
                        Download
                      </Button>
                    </div>
                  </div>

                  {/* Hover glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="relative py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 md:p-12 bg-white border border-slate-200 rounded-3xl shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-800 mb-4">
                Get More <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Resources</span>
              </h2>
              <p className="text-slate-500">
                Subscribe to receive exclusive templates, guides, and industry insights.
              </p>
            </div>

            <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                data-testid="newsletter-email-input"
                placeholder="Enter your email"
                className="flex-grow px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-cyan-500 transition-colors duration-300"
              />
              <Button
                type="submit"
                data-testid="newsletter-subscribe-btn"
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Need <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Personalized</span> Guidance?
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            Our senior consultants can help you apply these frameworks to your specific situation.
          </p>
          <Link to="/contact">
            <Button
              data-testid="resources-cta-btn"
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300"
            >
              Book a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ResourcesPage;
